import {
  cloneElement, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * Una pista que se puede LEER, tambien con el dedo.
 *
 * QUE PROBLEMA RESUELVE. Media aplicacion explicaba sus cifras con el atributo
 * `title` del navegador: que significa "Infravalorado", por que una muestra es
 * baja, que le falta al nucleo de un mazo. Es la peor manera de contar algo
 * importante, por tres motivos que se suman:
 *
 *   - EN EL TELEFONO NO EXISTE. No hay raton que posar, asi que esas
 *     explicaciones sencillamente no se ven nunca. Y el telefono es donde se
 *     consulta el meta entre ronda y ronda.
 *   - Tarda cerca de un segundo y sale con la tipografia del sistema, gris
 *     sobre amarillo: dentro de una interfaz cuidada canta como una scrollbar
 *     nativa.
 *   - Con teclado no hay forma de sacarlo si el elemento no es enfocable.
 *
 * COMO. Un envoltorio que no cambia lo que envuelve: la pista aparece al posar
 * el raton, al enfocar con el tabulador y al TOCAR (en el telefono el toque es
 * la unica forma de preguntar). Se va con Escape, al salir, o al tocar fuera.
 * El elemento envuelto recibe `aria-describedby`, que es como un lector de
 * pantalla anuncia "esto ademas se explica asi".
 *
 * SIN RETRASO AL APARECER, a diferencia del `title`: la pista se pide a
 * proposito -- se posa o se toca sobre una cifra que no se entiende -- y hacer
 * esperar a quien ya ha preguntado no protege de nada.
 *
 * EL GLOBO VIVE EN EL BODY, no dentro del envoltorio. Con `position: absolute`
 * quedaba a merced de cualquier antepasado con `overflow`, y el primer sitio
 * donde se uso ya lo demostro: la fila de chips de orden del meta se desplaza
 * en horizontal, o sea que RECORTA, y el globo caia 3 px por debajo de su
 * borde -- invisible justo en el telefono, que es para quien se hizo esto.
 * Sacandolo al body con posicion fija no hay caja que lo corte; el precio es
 * que la posicion se calcula a mano y hay que reencuadrarla al desplazar.
 */

/** Margen que se le deja al borde de la ventana al reencuadrar el globo. */
const MARGEN = 8;
/** Hueco entre el elemento explicado y su globo. */
const SEPARACION = 7;

export default function Pista({ texto, children, lado = 'arriba', className = '' }) {
  const [abierta, setAbierta] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const globoRef = useRef(null);
  const id = useId();

  /**
   * Donde cae el globo, en coordenadas de ventana.
   *
   * Se centra sobre lo que explica y despues se mete a la fuerza dentro de la
   * pantalla: el ultimo chip de una fila esta pegado al borde derecho y su
   * globo -- de hasta 280 px -- se salia media pantalla. Si del lado pedido no
   * cabe, se VUELCA al otro en vez de quedarse a medias fuera.
   */
  const situar = useCallback(() => {
    const globo = globoRef.current;
    const ancla = ref.current;
    if (!globo || !ancla) return;
    const caja = ancla.getBoundingClientRect();
    /* `offsetWidth/Height` y no el rectangulo: el globo lleva el `transform`
       de entrada y su rectangulo devuelve el valor a medio camino de la
       transicion, con lo que la posicion salia distinta en cada apertura. */
    const an = globo.offsetWidth;
    const al = globo.offsetHeight;

    const izquierda = Math.min(
      Math.max(MARGEN, caja.left + caja.width / 2 - an / 2),
      Math.max(MARGEN, window.innerWidth - MARGEN - an)
    );

    const cabeArriba = caja.top - SEPARACION - al >= MARGEN;
    const cabeAbajo = caja.bottom + SEPARACION + al <= window.innerHeight - MARGEN;
    const arriba = lado === 'arriba' ? cabeArriba || !cabeAbajo : !(cabeAbajo || !cabeArriba);

    setPos({
      left: Math.round(izquierda),
      top: Math.round(arriba ? caja.top - SEPARACION - al : caja.bottom + SEPARACION),
      arriba,
    });
  }, [lado]);

  useLayoutEffect(() => {
    if (!abierta) {
      setPos(null);
      return;
    }
    situar();
  }, [abierta, texto, situar]);

  useEffect(() => {
    if (!abierta) return undefined;
    /* Tocar fuera y Escape cierran. En captura para ganar a los manejadores
       de lo que haya debajo, que en las tablas del meta abren filas. */
    const fuera = (e) => {
      if (!ref.current?.contains(e.target)) setAbierta(false);
    };
    const tecla = (e) => e.key === 'Escape' && setAbierta(false);
    /* Al desplazar, el globo -- que es fijo -- se quedaria clavado mientras su
       ancla se va: se recoloca. `capture` para enterarse tambien del scroll de
       un contenedor interno, que no burbujea. */
    document.addEventListener('pointerdown', fuera, true);
    document.addEventListener('keydown', tecla, true);
    window.addEventListener('scroll', situar, { passive: true, capture: true });
    window.addEventListener('resize', situar);
    return () => {
      document.removeEventListener('pointerdown', fuera, true);
      document.removeEventListener('keydown', tecla, true);
      window.removeEventListener('scroll', situar, { capture: true });
      window.removeEventListener('resize', situar);
    };
  }, [abierta, situar]);

  if (!texto) return children ?? null;

  /*
   * `aria-describedby` va en el hijo y no en el envoltorio: es el control -- el
   * boton, el distintivo -- lo que la pista describe. Si el hijo no es un
   * elemento de React (un texto suelto), se sigue adelante sin el: mejor una
   * pista sin atar que un fallo.
   */
  const hijo = isValidElement(children)
    ? cloneElement(children, { 'aria-describedby': abierta ? id : undefined })
    : children;

  const globo = (
    <span
      role="tooltip"
      id={id}
      ref={globoRef}
      className={
        `pista__globo pista__globo--${pos?.arriba === false ? 'abajo' : 'arriba'}` +
        `${abierta && pos ? ' is-abierta' : ''}`
      }
      style={pos ? { left: `${pos.left}px`, top: `${pos.top}px` } : undefined}
      /* Fuera del arbol de accesibilidad mientras esta cerrada: si no, un
         lector la leeria siempre, tapada o no, y la pista dejaria de ser
         una respuesta a una pregunta para ser ruido de fondo. */
      aria-hidden={!abierta}
    >
      {texto}
    </span>
  );

  return (
    <span
      className={`pista ${className}`.trim()}
      ref={ref}
      /* El raton la abre al posarse. En un telefono el `pointerenter` llega
         con el toque, asi que el mismo gesto sirve para los dos sin escribir
         dos caminos. */
      onPointerEnter={() => setAbierta(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setAbierta(false)}
      onFocusCapture={() => setAbierta(true)}
      onBlurCapture={() => setAbierta(false)}
    >
      {hijo}
      {/* Al body: ningun `overflow` de en medio puede recortarlo. Se monta
          siempre (aunque cerrado) porque hay que poder MEDIRLO para saber
          donde ponerlo antes de enseñarlo. */}
      {createPortal(globo, document.body)}
    </span>
  );
}
